<template>
    <div class="summary-memory-parser-section">
        <h6 class="text-subtitle-2 font-weight-bold text-primary mb-2">Summary Memory (Parser Context)</h6>
        <div class="text-caption text-medium-emphasis mb-3">
            When enabled, an LLM rewrites recent conversation turns and the latest message into a standalone request
            for this node's parameter parser. The original user message is still used for fallback and final answers.
        </div>

        <v-switch :model-value="config.summaryMemoryEnabled" color="primary" hide-details density="compact" inset
            :readonly="readonly" label="Enable Summary Memory"
            @update:model-value="update('summaryMemoryEnabled', $event)" class="mb-3" />

        <template v-if="config.summaryMemoryEnabled">
            <v-autocomplete :model-value="config.summaryMemoryProvider" :items="providers" item-title="providerName"
                item-value="providerName" label="Provider" variant="outlined" density="compact" hide-details="auto"
                :readonly="readonly" @update:model-value="updateProvider" class="mb-3" />
            <v-autocomplete :model-value="config.summaryMemoryModel" :items="models" item-title="modelDisplay"
                item-value="modelName" label="Model" variant="outlined" density="compact" hide-details="auto"
                :readonly="readonly" :disabled="!config.summaryMemoryProvider"
                @update:model-value="update('summaryMemoryModel', $event)" class="mb-3" />
            <v-text-field v-if="config.summaryMemoryApiVersion" :model-value="config.summaryMemoryApiVersion"
                label="API Version" variant="outlined" density="compact" hide-details="auto" readonly class="mb-3" />

            <v-row>
                <v-col cols="6">
                    <v-text-field :model-value="config.summaryMemoryMemoryTurn" type="number" min="0" step="1"
                        label="Memory Turns" variant="outlined" density="compact" hide-details="auto" :readonly="readonly"
                        hint="0 = send the latest message directly" @focus="$event.target.select()"
                        @update:model-value="update('summaryMemoryMemoryTurn', $event)" />
                </v-col>
                <v-col cols="6">
                    <v-text-field :model-value="config.summaryMemoryMemoryDuration" type="number" min="1" step="1"
                        label="Memory Duration (min)" variant="outlined" density="compact" hide-details="auto"
                        :readonly="readonly" @focus="$event.target.select()"
                        @update:model-value="update('summaryMemoryMemoryDuration', $event)" />
                </v-col>
            </v-row>
            <v-row class="mt-1">
                <v-col cols="6">
                    <v-text-field :model-value="config.summaryMemoryTemperature" type="number" step="0.1" min="0"
                        max="2" label="Temperature" variant="outlined" density="compact" hide-details="auto"
                        :readonly="readonly" @update:model-value="update('summaryMemoryTemperature', $event)" />
                </v-col>
                <v-col cols="6">
                    <v-text-field :model-value="config.summaryMemoryTopP" type="number" step="0.1" min="0" max="1"
                        label="Top P" variant="outlined" density="compact" hide-details="auto" :readonly="readonly"
                        @update:model-value="update('summaryMemoryTopP', $event)" />
                </v-col>
            </v-row>
            <v-text-field :model-value="config.summaryMemoryMaxTokens" type="number" min="1" label="Max Tokens"
                variant="outlined" density="compact" hide-details="auto" :readonly="readonly" class="mt-3"
                @update:model-value="update('summaryMemoryMaxTokens', $event)" />
            <v-row class="mt-1">
                <v-col cols="6">
                    <v-text-field :model-value="config.summaryMemoryFrequencyPenalty" type="number" step="0.1" min="-2"
                        max="2" label="Frequency Penalty" variant="outlined" density="compact" hide-details="auto"
                        :readonly="readonly" @update:model-value="update('summaryMemoryFrequencyPenalty', $event)" />
                </v-col>
                <v-col cols="6">
                    <v-text-field :model-value="config.summaryMemoryPresencePenalty" type="number" step="0.1" min="-2"
                        max="2" label="Presence Penalty" variant="outlined" density="compact" hide-details="auto"
                        :readonly="readonly" @update:model-value="update('summaryMemoryPresencePenalty', $event)" />
                </v-col>
            </v-row>
            <v-textarea :model-value="config.summaryMemorySystemPrompt" label="System Prompt" variant="outlined"
                density="compact" auto-grow rows="6" hide-details="auto" :readonly="readonly" class="mt-3"
                placeholder="You are a conversation summarizer. Given the chat history and latest user message, write a concise standalone request that preserves the full context."
                @update:model-value="update('summaryMemorySystemPrompt', $event)" />
            <v-text-field :model-value="config.summaryMemoryTimeout" type="number" min="0" label="LLM Timeout (seconds)"
                variant="outlined" density="compact" hide-details="auto" :readonly="readonly" class="mt-3"
                hint="0 = use global timeout setting" @update:model-value="update('summaryMemoryTimeout', $event)" />
        </template>
    </div>
</template>

<script>
export default {
    name: 'SummaryMemoryParserSection',
    props: {
        config: { type: Object, required: true },
        providers: { type: Array, required: true },
        models: { type: Array, required: true },
        readonly: { type: Boolean, default: false },
    },
    emits: ['update-property', 'change-provider'],
    methods: {
        update(property, value) {
            this.$emit('update-property', property, value);
        },
        updateProvider(value) {
            this.update('summaryMemoryProvider', value);
            this.$emit('change-provider', value);
        },
    },
};
</script>

<style scoped>
.summary-memory-parser-section {
    margin: 24px 0 32px;
}
</style>
